import {
    alignButtonsInRows,
    eNotationInsertScenarios,
    getCommonNumberDeleteScenarios,
    getCommonNumberKeybindings,
    getCommonNumberKeyboardKeys,
    nonNumberInExponentScenario,
    scientificNotationInsertScenarios,
} from 'zycommon-web/utils/symbolic-tools/common-number';

// When not requiring units, remove the space, since just numerical answers are expected
const removeSpaceInsertScenario = {
    isScenarioPresent: value => value.includes('\\;'),
    handleScenario: editor => {
        // Remove the space
        editor.executeCommand('deleteBackward');
    },
};

/**
 * Get the delete scenarios for the Symbolic Number math live editor.
 * Note: The delete scenarios for Symbolic Number and Number and Units are the same.
 * @param {SymbolicShortAnswerQuestion} symbolicQuestion The symbolic short answer question containing the necessary tool-specific properties.
 * @param {boolean} symbolicQuestion.accept_E_notation Whether to accept E notation.
 * @param {boolean} symbolicQuestion.supportScientificNotation Whether to support scientific notation.
 * @returns {Array} The delete scenarios.
 */
export function getSymbolicNumberDeleteScenarios(symbolicQuestion) {
    const { accept_E_notation, supportScientificNotation } = symbolicQuestion;

    return getCommonNumberDeleteScenarios(accept_E_notation, supportScientificNotation);
}

/**
 * Get the insert scenarios for the Symbolic Number math live editor. The order in which the scenarios are added determines their precedence.
 * @param {SymbolicShortAnswerQuestion} symbolicQuestion The symbolic short answer question containing the necessary tool-specific properties.
 * @param {boolean} symbolicQuestion.accept_E_notation Whether to accept E notation.
 * @param {boolean} symbolicQuestion.supportScientificNotation Whether to support scientific notation.
 * @returns {Array} The insert scenarios.
 */
export function getSymbolicNumberInsertScenarios(symbolicQuestion) {
    const { accept_E_notation, supportScientificNotation } = symbolicQuestion;
    const insertScenarios = [];

    if (accept_E_notation) {
        insertScenarios.push(...eNotationInsertScenarios);
    }
    if (supportScientificNotation) {
        insertScenarios.push(...scientificNotationInsertScenarios, nonNumberInExponentScenario, removeSpaceInsertScenario);
    }

    return insertScenarios;
}

/**
 * Get the keybindings for the Symbolic Number math live editor.
 * @param {SymbolicShortAnswerQuestion} symbolicQuestion The symbolic short answer question containing the necessary tool-specific properties.
 * @param {boolean} symbolicQuestion.accept_E_notation Whether to accept E notation.
 * @param {boolean} symbolicQuestion.supportScientificNotation Whether to support scientific notation.
 * @returns {Array} The Symbolic Number keybindings.
 */
export function getSymbolicNumberKeybindings(symbolicQuestion) {
    const { accept_E_notation, supportScientificNotation } = symbolicQuestion;

    return getCommonNumberKeybindings(accept_E_notation, supportScientificNotation);
}

/**
 * The Symbolic Number virtual keyboard layout.
 * @param {Object} commonKeys The common keys used across different virtual keyboard layouts.
 * @param {SymbolicShortAnswerQuestion} symbolicQuestion The symbolic short answer question containing the necessary tool-specific properties.
 * @param {boolean} symbolicQuestion.accept_E_notation Whether to accept E notation.
 * @param {boolean} symbolicQuestion.hide_E_notation Whether to hide E notation.
 * @param {boolean} symbolicQuestion.supportScientificNotation Whether to support scientific notation.
 * @returns {Array} The keyboard layout.
 */
export function getSymbolicNumberKeyboardLayout(commonKeys, symbolicQuestion) {
    const { deleteKeyCap, moveLeftKeyCap, moveRightKeyCap, nextStepKeyCap } = commonKeys;
    const { accept_E_notation, hide_E_notation, supportScientificNotation } = symbolicQuestion;
    const rows = [];

    // Step 1: Get common keys (numbers, negate, decimal point, E notation, scientific notation)
    rows.push(...getCommonNumberKeyboardKeys(commonKeys, accept_E_notation, hide_E_notation, supportScientificNotation));

    // Step 2: Add control keys (move left, move right, Delete, Check)
    rows[2].push(moveLeftKeyCap, moveRightKeyCap);
    rows[rows.length - 1].push(deleteKeyCap, nextStepKeyCap);

    // Step 3: Align rows
    alignButtonsInRows(rows);

    return rows;
}
